import { ShoppingCart } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useCart } from "../context/CartContext";
import { formatCurrency } from "../utils/format";
import { Button } from "./ui/button";

export function ProductListRow({ product }) {
  const { addToCart, cartItems, loading, removeItem, updateQuantity } = useCart();
  const [imageError, setImageError] = useState(false);

  const uomOptions = product.dynamicUOM || [];
  const hasMultipleUoms = uomOptions.length > 1;
  const activeUom = hasMultipleUoms
    ? product.selectedUOM || uomOptions[0]
    : {
        uomId: 2,
        uomDesc: "UNIT",
        unitPerCarton: product.unitPerCarton || 1,
        price: product.price,
      };
  const displayPrice = Number(activeUom.price || product.price || 0);
  const productForCart = {
    ...product,
    price: displayPrice,
    uomId: activeUom.uomId,
    uomDesc: activeUom.uomDesc,
    unitPerCarton: activeUom.unitPerCarton || 1,
    selectedUOM: {
      uomId: activeUom.uomId,
      uomDesc: activeUom.uomDesc,
      unitPerCarton: activeUom.unitPerCarton || 1,
      price: displayPrice,
    },
  };
  const productIdKey = String(product.productId);
  const productUomId = Number(productForCart.uomId || 0);
  const cartItem = useMemo(
    () =>
      cartItems.find(
        (item) => {
          const itemProductId = String(item.productId ?? item.ProductID ?? item.ProductId ?? "");
          const itemUomId = Number(item.uomId ?? item.UOMID ?? 0);

          if (itemProductId !== productIdKey) {
            return false;
          }

          return !itemUomId || !productUomId || itemUomId === productUomId;
        }
      ) || null,
    [cartItems, productIdKey, productUomId]
  );

  async function handleAddToCart() {
    try {
      await addToCart(productForCart, 1);
    } catch (error) {
      // CartContext already captures the error for UI display.
    }
  }

  async function handleQuantityChange(nextQuantity) {
    if (!cartItem) {
      return;
    }

    try {
      if (nextQuantity <= 0) {
        await removeItem(cartItem);
        return;
      }

      await updateQuantity(cartItem, nextQuantity);
    } catch (error) {
      // CartContext already captures the error for UI display.
    }
  }

  return (
    <div className="grid gap-4 rounded-2xl border border-border bg-card p-3 shadow-sm transition hover:border-primary/30 hover:shadow-soft sm:grid-cols-[88px_minmax(0,1fr)_auto] sm:items-center">
      <Link
        className="block h-24 overflow-hidden rounded-xl bg-secondary sm:h-20"
        state={{ product }}
        to={`/products/${encodeURIComponent(product.productId)}`}
      >
        {product.imageUrl && !imageError ? (
          <img
            alt={product.productName}
            className="h-full w-full object-cover"
            loading="lazy"
            onError={() => setImageError(true)}
            src={product.imageUrl}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center px-2 text-center text-xs font-semibold text-muted-foreground">
            No Image Available
          </div>
        )}
      </Link>

      <div className="min-w-0">
        <Link
          className="block"
          state={{ product }}
          to={`/products/${encodeURIComponent(product.productId)}`}
        >
          <h3 className="truncate font-display text-lg font-semibold transition hover:text-primary">
            {product.productName}
          </h3>
        </Link>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          {product.productCode ? <span>Code: {product.productCode}</span> : null}
          <span>UOM: {activeUom.uomDesc || "UNIT"}</span>
        </div>
        <p className="mt-2 text-lg font-semibold text-primary">{formatCurrency(displayPrice)}</p>
      </div>

      <div className="flex flex-wrap items-center gap-3 sm:justify-end">
        {cartItem ? (
          <div className="flex h-11 min-w-36 items-center justify-between rounded-xl border border-primary bg-primary/5 px-2 text-primary transition">
            <button
              className="rounded-lg px-3 py-1 text-base font-bold transition hover:bg-primary hover:text-primary-foreground"
              disabled={loading}
              onClick={() => handleQuantityChange(cartItem.qty - 1)}
              type="button"
            >
              -
            </button>
            <span className="min-w-8 text-center text-sm font-bold">{cartItem.qty}</span>
            <button
              className="rounded-lg px-3 py-1 text-base font-bold transition hover:bg-primary hover:text-primary-foreground"
              disabled={loading}
              onClick={() => handleQuantityChange(cartItem.qty + 1)}
              type="button"
            >
              +
            </button>
          </div>
        ) : (
          <Button disabled={loading} onClick={handleAddToCart} type="button">
            <ShoppingCart className="h-4 w-4" />
            + Add to Cart
          </Button>
        )}
      </div>
    </div>
  );
}
